import IPoll, { PollType } from '../model/IPoll';
import {
    ICreatePollQuestion,
    ICreatePollRequest,
    IPollTemplate,
    IUpdatePollQuestion,
    PollQuestionType,
} from '../services/DataService';

/**
 * Editable question model shared by the wizard, the edit form and the
 * details page. The API returns questions in a few casing variants, so
 * everything is normalised here once.
 */
/** Answer option. `Id` is kept for saved options so edits stay linked to collected answers. */
export interface IEditableOption {
    Id?: string;
    Text: string;
}

export interface IEditableQuestion {
    /** Client-side key, stable across re-renders. */
    key: string;
    Id?: string;
    Type: PollQuestionType;
    Title: string;
    Required: boolean;
    Options: IEditableOption[];
    Placeholder: string;
    MinValue: number;
    MaxValue: number;
}

let keyCounter = 0;
export const nextQuestionKey = (): string => `q-${Date.now().toString(36)}-${(keyCounter += 1)}`;

/** Bounds of the rating scale; the backend rejects anything outside them. */
export const RATING_SCALE_MIN = 0;
export const RATING_SCALE_MAX = 10;

export const emptyOption = (): IEditableOption => ({ Text: '' });
const emptyOptions = (): IEditableOption[] => [emptyOption(), emptyOption()];

export const isChoiceType = (type: PollQuestionType): boolean =>
    type === PollQuestionType.SingleChoice || type === PollQuestionType.MultipleChoice;

export const isTextType = (type: PollQuestionType): boolean =>
    type === PollQuestionType.Text || type === PollQuestionType.WordCloud;

export const isRangeType = (type: PollQuestionType): boolean =>
    type === PollQuestionType.Rating || type === PollQuestionType.Nps;

export function getDefaultRange(type: PollQuestionType): { min: number; max: number } {
    return type === PollQuestionType.Nps ? { min: 0, max: 10 } : { min: 1, max: 5 };
}

export function createEmptyQuestion(type: PollQuestionType = PollQuestionType.Rating): IEditableQuestion {
    const range = getDefaultRange(type);
    return {
        key: nextQuestionKey(),
        Type: type,
        Title: '',
        Required: true,
        Options: isChoiceType(type) ? emptyOptions() : [],
        Placeholder: '',
        MinValue: range.min,
        MaxValue: range.max,
    };
}

/** Re-applies type-specific defaults after the type changes. */
export function applyQuestionType(question: IEditableQuestion, type: PollQuestionType): IEditableQuestion {
    const range = getDefaultRange(type);
    const keepOptions = isChoiceType(type) && isChoiceType(question.Type);
    return {
        ...question,
        Type: type,
        Options: isChoiceType(type) ? (keepOptions && question.Options.length >= 2 ? question.Options : emptyOptions()) : [],
        Placeholder: isTextType(type) ? question.Placeholder : '',
        MinValue: type === PollQuestionType.Rating ? question.MinValue || range.min : range.min,
        MaxValue: type === PollQuestionType.Rating ? question.MaxValue || range.max : range.max,
    };
}

const KNOWN_TYPES = new Set<number>([
    PollQuestionType.Rating,
    PollQuestionType.SingleChoice,
    PollQuestionType.MultipleChoice,
    PollQuestionType.Text,
    PollQuestionType.Nps,
    PollQuestionType.WordCloud,
]);

function parseType(value: unknown): PollQuestionType {
    const type = Number(value);
    return KNOWN_TYPES.has(type) ? (type as PollQuestionType) : PollQuestionType.Text;
}

function pick<T>(source: Record<string, unknown>, ...keys: string[]): T | undefined {
    for (const key of keys) {
        if (source[key] !== undefined && source[key] !== null) return source[key] as T;
    }
    return undefined;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function normalizeQuestion(raw: any): IEditableQuestion {
    const source: Record<string, unknown> = raw ?? {};
    const type = parseType(pick(source, 'Type', 'type', 'TypeCode', 'typeCode'));
    const rawOptions = pick<unknown[]>(source, 'Options', 'options', 'Choices', 'choices') ?? [];

    const options: IEditableOption[] = rawOptions
        .slice()
        .sort((a, b) => Number((a as Record<string, unknown>)?.Order ?? (a as Record<string, unknown>)?.order ?? 0)
            - Number((b as Record<string, unknown>)?.Order ?? (b as Record<string, unknown>)?.order ?? 0))
        .map((option): IEditableOption => (typeof option === 'string'
            ? { Text: option.trim() }
            : {
                Id: pick<string>(option as Record<string, unknown>, 'Id', 'id'),
                Text: String((option as Record<string, unknown>)?.Text ?? (option as Record<string, unknown>)?.text ?? '').trim(),
            }))
        .filter((option) => option.Text);

    const range = getDefaultRange(type);
    const required = pick<boolean>(source, 'Required', 'required', 'IsRequired', 'isRequired');

    return {
        key: nextQuestionKey(),
        Id: pick<string>(source, 'Id', 'id', 'QuestionId', 'questionId'),
        Type: type,
        Title: String(pick(source, 'Title', 'title', 'Text', 'text') ?? ''),
        Required: required === undefined ? true : !!required,
        Options: isChoiceType(type) ? (options.length >= 2 ? options : [...options, ...Array.from({ length: 2 - options.length }, emptyOption)]) : [],
        Placeholder: String(pick(source, 'Placeholder', 'placeholder') ?? ''),
        MinValue: isRangeType(type) ? Number(pick(source, 'MinValue', 'minValue') ?? range.min) : range.min,
        MaxValue: isRangeType(type) ? Number(pick(source, 'MaxValue', 'maxValue') ?? range.max) : range.max,
    };
}

export function getPollQuestions(poll: IPoll | undefined): IEditableQuestion[] {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const source = poll as any;
    const raw: unknown[] = Array.isArray(source?.Questions)
        ? source.Questions
        : Array.isArray(source?.questions)
            ? source.questions
            : [];
    const orderOf = (q: unknown, index: number): number => {
        const value = Number((q as Record<string, unknown>)?.Order ?? (q as Record<string, unknown>)?.order);
        return Number.isFinite(value) ? value : index;
    };
    return raw
        .map((q, index) => ({ q, order: orderOf(q, index), index }))
        .sort((a, b) => a.order - b.order || a.index - b.index)
        .map(({ q }) => normalizeQuestion(q));
}

export function questionFromTemplate(template: IPollTemplate, base?: IEditableQuestion): IEditableQuestion {
    const type = parseType(template.TypeCode);
    const choices: IEditableOption[] = (template.Choices ?? [])
        .slice()
        .sort((a, b) => a.Order - b.Order)
        .map((c) => ({ Text: c.Text }));
    const range = getDefaultRange(type);
    return {
        ...(base ?? createEmptyQuestion(type)),
        Type: type,
        Title: template.Text,
        Options: isChoiceType(type) ? (choices.length >= 2 ? choices : emptyOptions()) : [],
        Placeholder: isTextType(type) ? (template.Choices?.[0]?.Placeholder ?? '') : '',
        MinValue: range.min,
        MaxValue: range.max,
    };
}

/** Returns the first validation problem of a question, or undefined. */
export function validateQuestion(question: IEditableQuestion): string | undefined {
    if (!question.Title.trim()) return 'Укажите текст вопроса.';
    if (isChoiceType(question.Type) && question.Options.filter((o) => o.Text.trim()).length < 2) {
        return 'Добавьте минимум два варианта ответа.';
    }
    if (question.Type === PollQuestionType.Rating) {
        if (question.MinValue < RATING_SCALE_MIN || question.MaxValue > RATING_SCALE_MAX) {
            return `Шкала оценки должна быть в пределах от ${RATING_SCALE_MIN} до ${RATING_SCALE_MAX}.`;
        }
        if (question.MinValue >= question.MaxValue) {
            return 'Минимальное значение должно быть меньше максимального.';
        }
    }
    return undefined;
}

function toRequestQuestion(question: IEditableQuestion): IUpdatePollQuestion {
    const result: IUpdatePollQuestion = {
        Id: question.Id,
        Type: question.Type,
        Title: question.Title.trim(),
        Required: question.Required,
    };
    if (isChoiceType(question.Type)) {
        result.Options = question.Options
            .map((o) => ({ Id: o.Id, Text: o.Text.trim() }))
            .filter((o) => o.Text)
            .map((o, index) => ({ Id: o.Id, Text: o.Text, Order: index + 1 }));
    }
    if (isRangeType(question.Type)) {
        const range = question.Type === PollQuestionType.Nps
            ? getDefaultRange(question.Type)
            : { min: question.MinValue, max: question.MaxValue };
        result.MinValue = range.min;
        result.MaxValue = range.max;
    }
    if (isTextType(question.Type)) {
        result.Placeholder = question.Placeholder.trim() || undefined;
    }
    return result;
}

export const toUpdateQuestions = (questions: IEditableQuestion[]): IUpdatePollQuestion[] =>
    questions.map(toRequestQuestion);

export const toCreateQuestions = (questions: IEditableQuestion[]): ICreatePollQuestion[] =>
    questions.map((q) => {
        const { Id: _id, ...rest } = toRequestQuestion(q);
        void _id;
        return {
            ...rest,
            Options: rest.Options?.map(({ Text, Order }) => ({ Text, Order })),
        };
    });

/** Builds a create request that clones an existing poll (used by «Дублировать»). */
export function buildDuplicateRequest(poll: IPoll): ICreatePollRequest {
    const quick = poll.Type !== undefined ? poll.Type === PollType.Fast : !!poll.Quick;
    const questions = getPollQuestions(poll);
    return {
        Quick: quick,
        Type: quick ? PollType.Fast : PollType.Complex,
        Title: `${poll.Title ?? 'Опрос'} (копия)`,
        Description: poll.Description ?? undefined,
        EventTypeCode: poll.EventTypeCode,
        IsAnonymous: true,
        GeoRegionId: poll.GeoRegionId ?? undefined,
        GeoTerritoryId: poll.GeoTerritoryId ?? undefined,
        SingleResponse: !!poll.SingleResponse,
        CompletedDisplayResults: !!poll.CompletedDisplayResults,
        CompletedMessage: poll.CompletedMessage ?? undefined,
        Questions: toCreateQuestions(questions),
    };
}
